import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, ArrowRight, Clock } from 'lucide-react';
import { getCheckoutStatus, getSubscriptionStatus } from '../services/api';

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  // Initialize status based on whether sessionId exists
  const [status, setStatus] = useState(sessionId ? 'checking' : 'error');
  const [error, setError] = useState(sessionId ? null : 'No session ID found');
  const [checkoutData, setCheckoutData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const MAX_POLLS = 10; // Poll for up to 20 seconds
  const POLL_INTERVAL = 2000; // 2 seconds

  const checkSubscriptionActive = useCallback(async () => {
    try {
      const subStatus = await getSubscriptionStatus();
      return subStatus.is_member || subStatus.status === 'active' || subStatus.status === 'trialing';
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    // Skip if no sessionId (already handled in initial state)
    if (!sessionId) {
      return;
    }

    const checkStatus = async () => {
      try {
        // First check checkout session status
        const result = await getCheckoutStatus(sessionId);
        setCheckoutData(result);
        
        // Check if subscription is already active (via webhook or direct)
        if (result.is_active || result.subscription_status === 'active' || result.subscription_status === 'trialing') {
          setStatus('success');
          return;
        }
        
        // Also check subscription status directly
        const isActive = await checkSubscriptionActive();
        if (isActive) {
          setStatus('success');
          return;
        }
        
        // If payment is complete but subscription not yet active, keep polling
        if (result.payment_status === 'paid' || result.status === 'complete') {
          if (pollCount < MAX_POLLS) {
            // Show "processing" state after payment is confirmed
            setStatus('processing');
            setTimeout(() => setPollCount(c => c + 1), POLL_INTERVAL);
          } else {
            // After max polls, assume success (webhook will handle it)
            setStatus('success');
          }
          return;
        }
        
        // If still pending payment
        if (result.status === 'open') {
          if (pollCount < MAX_POLLS) {
            setTimeout(() => setPollCount(c => c + 1), POLL_INTERVAL);
          } else {
            setStatus('timeout');
          }
          return;
        }
        
        // If expired
        if (result.status === 'expired') {
          setStatus('expired');
          return;
        }
        
        // Keep polling if not at max
        if (pollCount < MAX_POLLS) {
          setTimeout(() => setPollCount(c => c + 1), POLL_INTERVAL);
        } else {
          setStatus('timeout');
        }
        
      } catch (err) {
        console.error('Error checking status:', err);
        
        // Better error handling for common issues
        const errorMessage = err.message || '';
        
        if (errorMessage.includes('not found or has expired') || 
            errorMessage.includes('No such checkout.session') || 
            errorMessage.includes('checkout session')) {
          setError('This checkout session has expired or is no longer valid. Please try again.');
        } else if (errorMessage.includes('Transaction not found')) {
          setError('We could not find your transaction. If you completed payment, please check your email for confirmation.');
        } else if (errorMessage.includes('Network error') || errorMessage.includes('unavailable')) {
          setError("We're having trouble connecting. Please try again in a moment.");
        } else {
          // For any other error, show a gentle message
          setError("We couldn't verify your payment right now. If you completed the checkout, your subscription should be active shortly.");
        }
        setStatus('error');
      }
    };

    checkStatus();
  }, [sessionId, pollCount, checkSubscriptionActive]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern">
      {/* Header */}
      <header className="border-b border-calm-border bg-fern-card/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5">
          <div 
            className="text-2xl font-bold text-calm-text cursor-pointer" 
            style={{fontFamily: 'DM Serif Display, serif'}}
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-20">
        <div className="bg-fern-card rounded-2xl p-10 border border-calm-border shadow-lg text-center">
          {status === 'checking' && (
            <div className="space-y-6">
              <Loader2 className="h-16 w-16 animate-spin text-forest mx-auto" />
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                Thanks. We're confirming your Circle status…
              </h1>
              <p className="text-calm-text-secondary">
                Please wait while we verify your subscription.
              </p>
            </div>
          )}

          {status === 'processing' && (
            <div className="space-y-6">
              <div className="relative">
                <Clock className="h-16 w-16 text-forest mx-auto" />
                <div className="absolute -bottom-1 -right-1 w-6 h-6">
                  <Loader2 className="h-6 w-6 animate-spin text-forest" />
                </div>
              </div>
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                Payment received
              </h1>
              <p className="text-calm-text-secondary">
                We're activating your Circle membership. This usually takes a few seconds.
              </p>
              {checkoutData?.mode === 'subscription' && (
                <p className="text-sm text-forest">
                  Subscription mode confirmed ✓
                </p>
              )}
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6 fade-in-up">
              <div className="w-20 h-20 bg-herbal-light rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-12 w-12 text-forest" />
              </div>
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                You're in.
              </h1>
              <p className="text-lg text-forest leading-relaxed">
                Welcome to the Circle.
              </p>
              {checkoutData?.mode === 'subscription' && (
                <p className="text-sm text-calm-text-secondary">
                  Your subscription will renew monthly at $9/month.
                </p>
              )}
              <div className="bg-herbal-light/50 rounded-xl p-6 border border-herbal text-left">
                <h3 className="font-medium text-calm-text mb-3">What's now available:</h3>
                <ul className="space-y-2 text-forest">
                  <li>• Long-term alignment memory</li>
                  <li>• Pooled action suggestions</li>
                  <li>• Quiet sponsorship opportunities</li>
                  <li>• Platform influence and experiments</li>
                </ul>
              </div>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl transition-colors"
              >
                Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <div className="space-y-6">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                {status === 'timeout' ? (
                  <Clock className="h-12 w-12 text-amber-600" />
                ) : (
                  <XCircle className="h-12 w-12 text-red-500" />
                )}
              </div>
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                {status === 'expired' ? 'Session Expired' : 
                 status === 'timeout' ? 'Still processing—check again in a moment' :
                 'Something went wrong'}
              </h1>
              <p className="text-calm-text-secondary max-w-md mx-auto">
                {status === 'timeout' 
                  ? "Your payment may still be processing. Check your dashboard or email for confirmation."
                  : error || 'We couldn\'t confirm your payment. Please try again or contact support.'}
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                {status !== 'timeout' && (
                  <Button
                    onClick={() => navigate('/circle')}
                    variant="outline"
                    className="border-calm-border text-forest hover:bg-fern px-8 py-6 text-base rounded-xl"
                  >
                    Try again
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
